import { capitalize, get, identity, last, split } from "lodash"

export const TOOLTIPS = {
  // The decomposition tooltips are simple text so they don't need to be functions or have label, description properties
  decomposition: {
    risk: "A risk factor attribution which decomposes drivers of predicted risk, providing an analysis of a position’s contribution (in variance) to total risk (standard deviation)",
    exposure: "Decomposes security drivers to the risk model’s factors, providing an analysis of a position’s contribution to the portfolio’s exposure",
    beta: "Evaluate which securities contribute most to the portfolio’s beta, which measures how the portfolio behaves relative to the broad market (as defined by the risk model universe)",
    predictedFactorSensitivity: "Expected portfolio return impact from factors based on factor exposure and expected factor volatility. Factor volatility is estimated using the risk model covariance matrix.",
    historicalFactorSensitivity: "Expected portfolio return impact from factors based on factor exposure and expected factor volatility. Factor volatility is estimated using historical realized factor returns.",
    marginal: "Evaluate the marginal impact on risk for any security",
  },
  risk: {
    total: (active) => ({
      html: <>
        <div className="label">Total {capitalize(active)} Risk</div>
        <span className="description">The predicted standard deviation of future returns</span>
        <hr />
        <div className="label">Position Total {capitalize(active)} Risk Contribution</div>
        <span className="description">The portion of total {active} risk for a particular position, out of 100% of total {active} risk</span>
      </>,
    }),
    specific: (active) => ({
      label: "Specific Risk - Decomposition",
      description: `The percentage of ${active} risk that is driven by specific risk` }),
    factor: (factor) => ({
      label: "Factor Risk - Decomposition",
      description: `The percentage of ${factor} risk that is driven by factor risk` }),
    assetTotal: () => ({
      label: "Asset Total Risk Contribution",
      description: "The portion of total risk for a particular position, out of 100% of total risk" }),
  },
  category: {
    risk: (category, active) => ({
      label: `${category} Risk`,
      description: `The percentage of ${active} risk that is driven by ${category}. Nets to factor risk`,
    }),
  },
  factor: {
    risk: (factor, active) => ({
      label: `${factor} Risk`,
      description: `The percentage of ${active} risk that is driven by ${factor}. Nets to category risk`,
    }),
    exposure: (factor, active) => ({
      label: `${factor} Exposure`,
      description: `The ${active} exposure of this portfolio to ${factor}`,
    }),
    beta: (factor, _, betaType) => {
      let label = `${factor} Beta`
      let descriptionFactor = factor
      if (betaType === "dynamicPredicted") {
        const betaInstrument = last(split(factor, " - "))
        label = `Custom Beta - ${betaInstrument}`
        descriptionFactor = "Custom"
      }
      return {
        label,
        description: `The security’s contribution to the portfolio’s ${descriptionFactor} Beta, as calculated by “Position's ${descriptionFactor} Beta * % equity”`,
      }
    },
    historicalFactorSensitivity: (factor) => ({
      label: `${factor} Historical Factor Sensitivity`,
      description: `The security’s contribution to the portfolio’s ${factor} Historical Factor Sensitivity, as calculated by “Position's ${factor} Historical Factor Sensitivity * % equity”`,
    }),
    predictedFactorSensitivity: (factor) => ({
      label: `${factor} Predicted Factor Sensitivity`,
      description: `The security’s contribution to the portfolio’s ${factor} Predicted Factor Sensitivity, as calculated by “Position's ${factor} Predicted Factor Sensitivity * % equity”`,
    }),
  },
}

function getActiveLabel(isActive) {
  return isActive ? "active" : "absolute"
}

function getAdjustedType(decompositionType, isContentSet = false) {
  return isContentSet ? "exposure" : decompositionType
}

export function riskTooltip(type, isActive) {
  const parameters = [getActiveLabel(isActive)]
  const tooltipFn = get(TOOLTIPS, ["risk", type], identity)

  return tooltipFn(...parameters)
}

export function categoryTooltip(category, decompositionType, isActive, isContentSet) {
  const parameters = [get(category, "label"), getActiveLabel(isActive)]
  const tooltipFn = get(TOOLTIPS, ["category", getAdjustedType(decompositionType, isContentSet)], identity)

  return tooltipFn(...parameters)
}

export function factorTooltip(factor, decompositionType, isActive, isContentSet, betaType) {
  const parameters = [factor.label, getActiveLabel(isActive), betaType]
  const tooltipFn = get(TOOLTIPS, ["factor", getAdjustedType(decompositionType, isContentSet)], identity)

  return tooltipFn(...parameters)
}
